import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { XMarkIcon, MapPinIcon, ClockIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";

export interface TrainTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  trainNo: string;
  trainName?: string;
  defaultDate?: string; // Format: "DD-MMM-YYYY" e.g., "24-Feb-2026" or date string that can be parsed
}

const TrainTrackingModal: React.FC<TrainTrackingModalProps> = ({ 
  isOpen, 
  onClose, 
  trainNo, 
  trainName = "",
  defaultDate 
}) => {
  const [trackingData, setTrackingData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [expandedStation, setExpandedStation] = useState<number | null>(null);
  
  const currentStationRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to current station
  const scrollToCurrentStation = () => {
    setTimeout(() => {
      if (currentStationRef.current && scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const element = currentStationRef.current;
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        
        // Calculate scroll position to center the element
        const scrollTop = element.offsetTop - container.offsetTop - (containerRect.height / 2) + (elementRect.height / 2);
        container.scrollTo({ top: Math.max(0, scrollTop), behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    if (isOpen && trainNo) {
      fetchTrackingData();
    }
    return () => {
      setTrackingData(null);
      setError(null);
      setSelectedDate("");
    };
  }, [isOpen, trainNo]);

  const fetchTrackingData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`https://bookmytrain.vercel.app/api/livestatus?trainNumber=${trainNo}`);
      const data = await response.json();
      if (data && data.trainNo) {
        setTrackingData(data);
        if (data.availableDates?.length > 0) {
          // Priority: 1) defaultDate prop, 2) today's date, 3) first available date
          let dateToSelect = data.availableDates[0];
          
          // Try defaultDate first (convert to expected format if needed)
          if (defaultDate) {
            const defaultDateStr = moment(defaultDate).format("DD-MMM-YYYY");
            const foundDefault = data.availableDates.find((d: string) => d === defaultDateStr);
            if (foundDefault) {
              dateToSelect = foundDefault;
            }
          }
          
          // If no defaultDate match, try today's date
          if (!defaultDate || !data.availableDates.find((d: string) => d === moment(defaultDate).format("DD-MMM-YYYY"))) {
            const todayStr = moment().format("DD-MMM-YYYY");
            const todayDate = data.availableDates.find((d: string) => d === todayStr);
            if (todayDate) {
              dateToSelect = todayDate;
            }
          }
          
          setSelectedDate(dateToSelect);
        }
        // Scroll to current station after data loads
        scrollToCurrentStation();
      } else {
        setError("No tracking data available for this train");
      }
    } catch (err) {
      setError("Failed to fetch tracking data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getDelayColor = (delay: string) => {
    if (!delay || delay === "On Time" || delay === "") return "text-green-600";
    if (delay.includes("Hr")) return "text-red-600";
    const mins = parseInt(delay);
    if (mins <= 15) return "text-yellow-600";
    if (mins <= 30) return "text-orange-600";
    return "text-red-600";
  };

  const getStationStatus = (station: any) => {
    const actual = station.arrival?.actual || station.departure?.actual;
    if (!actual) return "upcoming";
    if (actual === "Cancelled") return "cancelled";
    if (actual === "Non-Reporting Station") return "non-reporting";
    if (actual.includes("*")) return "expected";
    return "passed";
  };

  const getCurrentStationIndex = (stations: any[]) => {
    for (let i = stations.length - 1; i >= 0; i--) {
      const status = getStationStatus(stations[i]);
      if (status === "passed" && stations[i].stationCode) return i;
    }
    return -1;
  };

  // Calculate time since departure for current station
  const getTimeSinceDeparture = (station: any) => {
    if (!station?.departure?.actual || station.departure.actual === "DSTN") return null;
    try {
      // Parse actual departure time like "18:23 23-Feb"
      const actualDep = station.departure.actual.replace("*", "");
      const match = actualDep.match(/(\d{1,2}):(\d{2})\s*(\d{1,2})-(\w+)/);
      if (!match) return null;
      
      const [, hours, mins, day, month] = match;
      const monthMap: any = { "Jan": 0, "Feb": 1, "Mar": 2, "Apr": 3, "May": 4, "Jun": 5, "Jul": 6, "Aug": 7, "Sep": 8, "Oct": 9, "Nov": 10, "Dec": 11 };
      
      const depTime = moment()
        .month(monthMap[month])
        .date(parseInt(day))
        .hour(parseInt(hours))
        .minute(parseInt(mins))
        .second(0);
      
      const now = moment();
      const diffMins = now.diff(depTime, 'minutes');
      
      if (diffMins < 0) return null; // Future departure
      if (diffMins === 0) return "just now";
      if (diffMins < 60) return `${diffMins} min ago`;
      const diffHours = Math.floor(diffMins / 60);
      const remainingMins = diffMins % 60;
      if (remainingMins === 0) return `${diffHours}h ago`;
      return `${diffHours}h ${remainingMins}m ago`;
    } catch {
      return null;
    }
  };

  // Check if train is still at station (arrived but not departed)
  const isStandingAtStation = (station: any) => {
    if (!station?.arrival?.actual || station.arrival.actual === "SRC") return false;
    if (station.arrival.actual.includes("*")) return false; // Expected, not actual
    if (!station?.departure?.actual || station.departure.actual === "DSTN") return false;
    if (station.departure.actual.includes("*")) return true; // Arrived but departure is expected
    return false;
  };

  if (!isOpen) return null;

  const currentRun = trackingData?.runs?.[selectedDate];
  const stations = currentRun?.stations?.filter((s: any) => s.stationCode) || [];
  const currentIndex = getCurrentStationIndex(stations);
  const progress = stations.length > 0 ? ((currentIndex + 1) / stations.length) * 100 : 0;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header - Compact Layout */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white px-4 py-2">
            <div className="flex justify-between items-center gap-4">
              {/* Left: Train info */}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold truncate">{trainNo} - {trackingData?.trainName || trainName}</h2>
                {currentRun && (
                  <p className="text-blue-100 text-xs truncate">{currentRun.statusNote}</p>
                )}
              </div>
              
              {/* Center: Progress */}
              {stations.length > 0 && (
                <div className="flex-1 max-w-xs hidden sm:block">
                  <div className="flex justify-between text-[10px] text-blue-200 mb-0.5">
                    <span>{stations[0]?.stationCode}</span>
                    <span>{Math.round(progress)}%</span>
                    <span>{stations[stations.length - 1]?.stationCode}</span>
                  </div>
                  <div className="h-1.5 bg-blue-900 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-green-400 transition-all duration-500 rounded-full"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
              
              {/* Right: Controls */}
              <div className="flex items-center gap-2">
                {trackingData?.availableDates?.length > 1 && (
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="bg-blue-700 text-white px-2 py-1 rounded text-xs border border-blue-500"
                  >
                    {trackingData.availableDates.map((date: string) => (
                      <option key={date} value={date}>{date}</option>
                    ))}
                  </select>
                )}
                {currentRun?.lastUpdate && (
                  <span className="text-blue-200 text-[10px] hidden md:flex items-center gap-1">
                    <ClockIcon className="h-3 w-3" />
                    {currentRun.lastUpdate}
                  </span>
                )}
                <button onClick={onClose} className="p-1 hover:bg-blue-700 rounded-full transition-colors">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div ref={scrollContainerRef} className="overflow-y-auto max-h-[calc(90vh-120px)]">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Fetching live status...</span>
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                <ExclamationTriangleIcon className="h-16 w-16 text-red-400 mb-4" />
                <p className="text-red-600 font-medium">{error}</p>
                <button 
                  onClick={fetchTrackingData}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && stations.length > 0 && (
              <div className="p-4">
                {/* Station List */}
                <div className="relative">
                  {stations.map((station: any, index: number) => {
                    const status = getStationStatus(station);
                    const isCurrent = index === currentIndex;
                    const isPassed = index <= currentIndex;
                    const isExpanded = expandedStation === index;

                    return (
                      <div 
                        key={index} 
                        className="relative flex"
                        ref={isCurrent ? currentStationRef : null}
                      >
                        {/* Timeline */}
                        <div className="flex flex-col items-center mr-4">
                          <div className={`w-4 h-4 rounded-full border-2 z-10 ${
                            status === "cancelled" ? "bg-red-500 border-red-500" :
                            isCurrent ? "bg-blue-600 border-blue-600 animate-pulse" :
                            isPassed ? "bg-green-500 border-green-500" :
                            "bg-white border-gray-300"
                          }`}>
                            {isCurrent && (
                              <div className="absolute -left-1 -top-1 w-6 h-6 bg-blue-400 rounded-full animate-ping opacity-50" />
                            )}
                          </div>
                          {index < stations.length - 1 && (
                            <div className={`w-0.5 flex-1 min-h-[60px] ${
                              isPassed ? "bg-green-500" : "bg-gray-200"
                            }`} />
                          )}
                        </div>

                        {/* Station Card */}
                        <div 
                          className={`flex-1 mb-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            isCurrent ? "bg-amber-100 border-amber-400 shadow-lg ring-2 ring-amber-300" :
                            status === "cancelled" ? "bg-red-50 border-red-200" :
                            isPassed ? "bg-green-50 border-green-200" :
                            "bg-gray-50 border-gray-200 hover:bg-gray-100"
                          }`}
                          onClick={() => setExpandedStation(isExpanded ? null : index)}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-gray-900">{station.stationCode}</span>
                                <span className="text-gray-600 text-sm">{station.stationName}</span>
                                {station.platform && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                    PF {station.platform}
                                  </span>
                                )}
                                {station.distanceKm && (
                                  <span className="text-gray-400 text-xs">{station.distanceKm} km</span>
                                )}
                              </div>
                              
                              {/* Timing details */}
                              <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Arr: </span>
                                  <span className="font-medium">{station.arrival?.scheduled || "-"}</span>
                                  {station.arrival?.actual && station.arrival.actual !== "SRC" && (
                                    <>
                                      <span className="text-gray-400"> → </span>
                                      <span className={`font-medium ${getDelayColor(station.arrival?.delay)}`}>
                                        {station.arrival.actual}
                                      </span>
                                      {station.arrival?.delay && station.arrival.delay !== "On Time" && (
                                        <span className={`ml-1 text-xs ${getDelayColor(station.arrival?.delay)}`}>
                                          ({station.arrival.delay})
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                                <div>
                                  <span className="text-gray-500">Dep: </span>
                                  <span className="font-medium">{station.departure?.scheduled || "-"}</span>
                                  {station.departure?.actual && station.departure.actual !== "DSTN" && (
                                    <>
                                      <span className="text-gray-400"> → </span>
                                      <span className={`font-medium ${getDelayColor(station.departure?.delay)}`}>
                                        {station.departure.actual}
                                      </span>
                                      {station.departure?.delay && station.departure.delay !== "On Time" && (
                                        <span className={`ml-1 text-xs ${getDelayColor(station.departure?.delay)}`}>
                                          ({station.departure.delay})
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Coach Position (expanded view) */}
                              {isExpanded && station.coachPosition?.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <p className="text-xs text-gray-500 mb-2">Coach Position:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {station.coachPosition.map((coach: any, ci: number) => (
                                      <span 
                                        key={ci}
                                        className={`px-2 py-0.5 text-xs rounded ${
                                          coach.type === "ENG" ? "bg-gray-700 text-white" :
                                          coach.type === "SLRD" ? "bg-yellow-100 text-yellow-800" :
                                          coach.type === "GEN" ? "bg-gray-200 text-gray-700" :
                                          coach.type === "2A" ? "bg-blue-100 text-blue-800" :
                                          coach.type === "3A" ? "bg-green-100 text-green-800" :
                                          coach.type === "SL" ? "bg-orange-100 text-orange-800" :
                                          "bg-gray-100 text-gray-600"
                                        }`}
                                      >
                                        {coach.number}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Status indicator */}
                            <div className="ml-2">
                              {status === "cancelled" && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                  Cancelled
                                </span>
                              )}
                              {status === "non-reporting" && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                  No Data
                                </span>
                              )}
                              {status === "expected" && (
                                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                                  Expected
                                </span>
                              )}
                              {isCurrent && (
                                <div className="flex flex-col items-end gap-1">
                                  <span className="px-2 py-1 bg-amber-500 text-white text-xs rounded-full font-bold animate-pulse">
                                    Current
                                  </span>
                                  <span className="text-[10px] text-amber-700 font-medium">
                                    {isStandingAtStation(station) 
                                      ? "🚉 Standing at station" 
                                      : getTimeSinceDeparture(station) 
                                        ? `🚂 Departed ${getTimeSinceDeparture(station)}`
                                        : null
                                    }
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {!loading && !error && stations.length === 0 && trackingData && (
              <div className="flex flex-col items-center justify-center py-20 text-center px-4">
                <MapPinIcon className="h-16 w-16 text-gray-300 mb-4" />
                <p className="text-gray-500">No station data available for this date</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t bg-gray-50 px-4 py-3 flex justify-between items-center">
            <span className="text-xs text-gray-500">
              {stations.length > 0 && `${stations.length} stations • ${currentIndex + 1} passed`}
            </span>
            <button
              onClick={fetchTrackingData}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrainTrackingModal;
